import Button from '@/common/components/Button'
import { DocumentFileList } from '@/common/components/DocumentFileList'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import Skeleton from '@/common/components/Skeleton'
import Table from '@/common/components/Table'
import { priceSurveyStatusStrategy } from '@/common/constants/priceSurveyStatusOptions'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { ApprovePriceSurveyModal } from '@/features/privateCompanies/components/PriceSurvey/AprovePriceSurveyModal'
import { RejectPriceSurveyModal } from '@/features/privateCompanies/components/PriceSurvey/RejectPriceSurveyModal'
import { companyPriceSurveyProductsColumns } from '@/features/privateCompanies/constants/priceSurvey/companyPriceSurveyProductsColumns.constant'
import { useCompanyPriceSurvey } from '@/features/privateCompanies/hooks/priceSurvey/useCompanyPriceSurvey'
import { DisablePriceSurveyModal } from '@/features/privateCompanies/pages/CompanyPriceSurvey/Show/components/DisablePriceSurveyModal'
import { CompanyPriceSurveyProductsTransformer } from '@/features/privateCompanies/services/priceSurvey/companyPriceSurveyProductsTransformer'
import { ChevronsRightIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

const Show = () => {
  const { companyId, id } = useParams()

  const {
    data: companyPriceSurvey,
    isPending: companyPriceSurveyIsPending,
    error: companyPriceSurveyError = {} as any,
    refetch: refetchCompanyPriceSurvey,
  } = useCompanyPriceSurvey({ id: id || '' })

  const tableMinHeight = 84 + Math.min(Math.max(1, companyPriceSurvey?.price_survey_products?.length || 1), 3) * 52

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={companyPriceSurveyError?.status}
      refetchFunction={refetchCompanyPriceSurvey}
      notFoundFallback={`/companies/${companyId}/price-survey`}
      notFoundButtonText="Ir para Pesquisa de preço"
    >
      <div className="flex flex-col gap-y-6 text-white-800">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-x-2">
            <GoBackButton fallback={`/companies/${companyId}/price-survey`} searchLastRouteFromFallback />

            <div className="flex items-center">
              <p className="text-white-400 font-normal">Pesquisa de preços</p>

              <ChevronsRightIcon strokeWidth={1} className="size-8 text-white-400" />

              {companyPriceSurveyIsPending ? (
                <Skeleton className="w-44" />
              ) : (
                <p>Pesquisa {dateTransformer(companyPriceSurvey?.survey_conducted_at || '')}</p>
              )}
            </div>
          </div>

          {!companyPriceSurveyIsPending && (
            <div className="flex items-center gap-x-2">
              {companyPriceSurvey?.status === 'pending' && (
                <>
                  <RejectPriceSurveyModal disabled={companyPriceSurveyIsPending} />

                  <ApprovePriceSurveyModal disabled={companyPriceSurveyIsPending} />

                  <Link
                    to={companyPriceSurveyIsPending ? '' : `/companies/${companyId}/price-survey/${id}/edit`}
                    className={companyPriceSurveyIsPending ? 'cursor-default' : ''}
                  >
                    <Button
                      operationKlassToCheckPermission="Admin::Client::PriceSurveys::Update"
                      data-testid="access_price_survey_update_button"
                      disabled={companyPriceSurveyIsPending}
                    >
                      Editar
                    </Button>
                  </Link>
                </>
              )}

              {companyPriceSurvey?.status === 'approved' && (
                <>
                  <DisablePriceSurveyModal priceSurveyId={id} />

                  <Link
                    to={companyPriceSurveyIsPending ? '' : `/companies/${companyId}/price-survey/${id}/edit`}
                    className={companyPriceSurveyIsPending ? 'cursor-default' : ''}
                  >
                    <Button
                      operationKlassToCheckPermission="Admin::Client::PriceSurveys::Update"
                      data-testid="access_price_survey_update_button"
                      disabled={companyPriceSurveyIsPending}
                    >
                      Editar
                    </Button>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-y-6 overflow-y-auto max-h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados da pesquisa</p>

          <div className="flex flex-col gap-y-2">
            <div>
              <p className="text-sm font-light italic">Status</p>

              {companyPriceSurveyIsPending ? (
                <Skeleton className="w-32 h-7" />
              ) : (
                <p
                  className={cn('text-lg font-bold', {
                    'text-warning': companyPriceSurvey?.status === 'pending',
                    'text-danger-soft': companyPriceSurvey?.status === 'rejected',
                    'text-valid': companyPriceSurvey?.status === 'approved',
                    'text-white-300': companyPriceSurvey?.status === 'disabled',
                  })}
                >
                  {priceSurveyStatusStrategy[companyPriceSurvey?.status as keyof typeof priceSurveyStatusStrategy] ||
                    ''}
                </p>
              )}
            </div>

            {companyPriceSurvey?.status === 'rejected' && (
              <div>
                <p className="text-sm font-light italic">Justificativa do Status</p>

                <p className="text-lg font-bold">{companyPriceSurvey.rejection_reason || '--'}</p>
              </div>
            )}

            <div>
              <p className="text-sm font-light italic">Cidade</p>

              {companyPriceSurveyIsPending ? (
                <Skeleton className="w-40 h-7" />
              ) : (
                <p className="text-lg font-bold">
                  {companyPriceSurvey?.geo_geoables?.map(({ geo }) => geo.display_name).join(', ') || ''}
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-light italic">Data da pesquisa</p>

              {companyPriceSurveyIsPending ? (
                <Skeleton className="w-32 h-7" />
              ) : (
                <p className="text-lg font-bold">{dateTransformer(companyPriceSurvey?.survey_conducted_at || '')}</p>
              )}
            </div>
          </div>

          <Table
            style={{ minHeight: `${tableMinHeight}px` }}
            rows={CompanyPriceSurveyProductsTransformer.tableTransformer()(
              companyPriceSurvey?.price_survey_products || [],
            )}
            columns={companyPriceSurveyProductsColumns}
            isLoadingData={companyPriceSurveyIsPending}
            page={1}
            totalPages={1}
          />

          <div className="flex flex-col gap-y-2">
            <p className="text-lg font-bold">Anexos</p>

            <DocumentFileList
              files={
                companyPriceSurvey?.evidences?.map((evidence) => ({
                  id: evidence.id,
                  fileName: getFilenameFromUrl(evidence?.url),
                  currentFileUrl: evidence?.url,
                })) || []
              }
            />
          </div>
        </div>
      </div>
    </ErrorMessageWrapper>
  )
}

export default Show
